import React from 'react'
import styled from 'styled-components'
import useLocale from 'js/hooks/useLocale'
import NovoNordiskLogo from 'media/novo-nordisk-logo.svg?svgr-webpack'

interface Glp1ButtonProps {
  href: string
  children: React.ReactNode
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
  isSimple?: boolean
}

const StyledButton = styled.a<{ $isNorwegian: boolean; $isSwedish: boolean }>`
  background: ${({ $isSwedish }) =>
    $isSwedish ? '#002060' : 'var(--color-primary)'};
  color: ${({ $isSwedish }) =>
    $isSwedish ? '#FFF' : 'var(--color-nn-accent)'};
  border: none;
  border-radius: var(--border-radius-md);
  padding: ${({ $isNorwegian, $isSwedish }) =>
    $isNorwegian
      ? 'var(--spacing-sm) var(--spacing-lg)'
      : $isSwedish
      ? 'var(--spacing-sm) 16px'
      : 'var(--spacing-sm) var(--spacing-md)'};
  font-weight: 600;
  font-size: var(--font-size-sm);
  text-decoration: none;
  text-transform: ${({ $isSwedish }) => ($isSwedish ? 'none' : 'uppercase')};
  display: flex;
  align-items: center;
  max-width: 300px;
  text-align: left;
  line-height: 1.3;
  transition:
    background var(--transition-base),
    color var(--transition-base);
  &:hover,
  &:focus {
    background: ${({ $isSwedish }) =>
      $isSwedish ? '#002060' : 'var(--color-nn-accent)'};
    color: ${({ $isSwedish }) =>
      $isSwedish ? '#FFF' : 'var(--color-nn-dark-forest)'};
  }
  @media (min-width: 768px) {
    font-size: var(--font-size-xs);
  }
`

const ButtonTextWrapper = styled.div<{ $isSwedish: boolean }>`
  display: ${({ $isSwedish }) => ($isSwedish ? 'flex' : 'block')};
  flex-direction: ${({ $isSwedish }) => ($isSwedish ? 'column' : 'row')};
  align-items: ${({ $isSwedish }) => ($isSwedish ? 'flex-start' : 'center')};
  flex: 1;
`

const ButtonTextLine = styled.span<{
  $isSecondLine?: boolean
  $isFirstLine?: boolean
  $isLastLine?: boolean
  $isSwedish?: boolean
}>`
  ${({ $isFirstLine, $isSwedish }) =>
    $isFirstLine &&
    $isSwedish &&
    `
    font-size: 16px;
    font-weight: 700;
    line-height: 27px;
  `}
  ${({ $isFirstLine, $isSwedish }) =>
    $isFirstLine &&
    !$isSwedish &&
    `
    font-size: 14px;
    padding-right: 24px;
  `}
  ${({ $isSecondLine, $isSwedish }) =>
    $isSecondLine &&
    $isSwedish &&
    `
    font-size: 16px;
    font-weight: 450;
    line-height: 21px;
    margin-bottom: 17px;
  `}
  ${({ $isSecondLine, $isSwedish }) =>
    $isSecondLine &&
    !$isSwedish &&
    `
    font-size: 8px;
    display: block;
    margin-top: 2px;
  `}
  ${({ $isLastLine, $isSwedish }) =>
    $isLastLine &&
    $isSwedish &&
    `
    font-size: 10px;
    font-weight: 600;
    line-height: 28px;
    letter-spacing: 1.12px;
    text-transform: uppercase;
    border-top: 1px solid #fff;
  `}
`
const LogoWrapper = styled.div`
  svg {
    width: 34px;
    height: 24px;
  }
  margin-left: var(--spacing-lg);
  @media (min-width: 768px) {
    margin-left: var(--spacing-xs);
  }
`

const Glp1Button: React.FC<Glp1ButtonProps> = ({
  href,
  children,
  onClick,
  isSimple,
}) => {
  const { language } = useLocale()
  const isNorwegian = language === 'no'
  const isSwedish = language === 'sv'

  const renderButtonContent = () => {
    if (isSwedish && typeof children === 'string') {
      const parts = children.split('_').map((part) => part.trim())
      if (parts.length >= 3) {
        return (
          <ButtonTextWrapper $isSwedish={isSwedish}>
            <ButtonTextLine $isFirstLine $isSwedish={isSwedish}>
              {parts[0]}
            </ButtonTextLine>
            <ButtonTextLine $isSecondLine $isSwedish={isSwedish}>
              {parts[1]}
            </ButtonTextLine>
            <ButtonTextLine $isLastLine $isSwedish={isSwedish}>
              {parts.slice(2).join('_')}
            </ButtonTextLine>
          </ButtonTextWrapper>
        )
      } else if (parts.length >= 2) {
        // Fallback for two parts: first line, second line
        return (
          <ButtonTextWrapper $isSwedish={isSwedish}>
            <ButtonTextLine $isFirstLine $isSwedish={isSwedish}>
              {parts[0]}
            </ButtonTextLine>
            <ButtonTextLine $isSecondLine $isSwedish={isSwedish}>
              {parts.slice(1).join('_')}
            </ButtonTextLine>
          </ButtonTextWrapper>
        )
      }
    }
    return <ButtonTextWrapper $isSwedish={false}>{children}</ButtonTextWrapper>
  }

  return (
    <StyledButton
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      $isNorwegian={isNorwegian}
      $isSwedish={isSwedish}
      onClick={onClick}
    >
      {renderButtonContent()}
      {!isSimple && !isSwedish && (
        <LogoWrapper>
          <NovoNordiskLogo />
        </LogoWrapper>
      )}
    </StyledButton>
  )
}

export default Glp1Button
